import { useEffect, useState } from 'react';
import { PERIOD_PRESETS, useAdminPeriod } from './useAdminPeriod';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function todayBRT(): string {
  // 'en-CA' formata como YYYY-MM-DD; TZ fixa em São Paulo (Brasil não tem mais DST)
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

const pillBase: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

const dateInputStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 10,
  border: '1px solid var(--border-soft)',
  background: 'var(--cream)',
  color: 'var(--text-body)',
  fontSize: 12,
  fontFamily: 'inherit',
  outline: 'none',
  colorScheme: 'light dark',
};

/**
 * Controle de período: pílulas de preset (7d/30d/90d/1 ano/Tudo) + "Personalizado"
 * que abre dois date pickers (De / Até) e um botão Aplicar.
 */
export function PeriodSelector() {
  const { isCustom, preset, from, to, setPreset, setCustom } = useAdminPeriod();

  const [open, setOpen] = useState(isCustom);
  const [localFrom, setLocalFrom] = useState(from);
  const [localTo, setLocalTo] = useState(to);

  // Mantém os inputs em sincronia quando o intervalo muda pela URL (ex.: navegação).
  useEffect(() => {
    setLocalFrom(from);
    setLocalTo(to);
    if (isCustom) setOpen(true);
  }, [from, to, isCustom]);

  const max = todayBRT();
  const canApply = DATE_RE.test(localFrom) && DATE_RE.test(localTo) && localTo >= localFrom;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {PERIOD_PRESETS.map((opt) => {
          const active = !isCustom && preset === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => {
                setOpen(false);
                setPreset(opt.value);
              }}
              style={{
                ...pillBase,
                borderColor: active ? 'var(--earth)' : 'var(--border-soft)',
                background: active ? 'var(--earth)' : 'var(--badge-bg)',
                color: active ? 'var(--cream)' : 'var(--text-strong)',
              }}
            >
              {opt.label}
            </button>
          );
        })}
        <button
          onClick={() => setOpen((o) => !o)}
          style={{
            ...pillBase,
            borderColor: isCustom ? 'var(--gold)' : 'var(--border-soft)',
            background: isCustom ? 'var(--gold)' : 'var(--badge-bg)',
            color: isCustom ? 'var(--gold-ink)' : 'var(--text-strong)',
            boxShadow: isCustom ? '0 4px 14px rgba(255,108,108,0.34)' : 'none',
          }}
        >
          📅 {isCustom ? `${formatBR(from)} – ${formatBR(to)}` : 'Personalizado'}
        </button>
      </div>

      {open && (
        <div
          className="admin-glass"
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 8,
            flexWrap: 'wrap',
            padding: '10px 12px',
            borderRadius: 14,
            background: 'var(--surface-white)',
            justifyContent: 'flex-end',
          }}
        >
          <Field label="De">
            <input
              type="date"
              value={localFrom}
              max={localTo || max}
              onChange={(e) => setLocalFrom(e.target.value)}
              style={dateInputStyle}
            />
          </Field>
          <Field label="Até">
            <input
              type="date"
              value={localTo}
              min={localFrom || undefined}
              max={max}
              onChange={(e) => setLocalTo(e.target.value)}
              style={dateInputStyle}
            />
          </Field>
          <button
            disabled={!canApply}
            onClick={() => canApply && setCustom(localFrom, localTo)}
            style={{
              ...pillBase,
              padding: '7px 16px',
              border: 'none',
              background: canApply ? 'var(--gold)' : 'var(--badge-bg)',
              color: canApply ? 'var(--gold-ink)' : 'var(--text-soft)',
              cursor: canApply ? 'pointer' : 'not-allowed',
              boxShadow: canApply ? '0 4px 14px rgba(255,108,108,0.30)' : 'none',
            }}
          >
            Aplicar
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function formatBR(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}
